import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

/**
 * Data hooks for the Knowledge Base monitor — a reader over the
 * `cinch_knowledge_base` Qdrant collection and the kb-pipeline status API,
 * plus the review state this app owns.
 *
 * Same conventions as code_rag: overview/articles/runs are cached queries;
 * search is a query keyed on URL state (idempotent, cacheable, no mount
 * effect); Ask is a mutation (an LLM turn must never re-run on focus).
 */

export type RunResult =
  | "ok"
  | "ok_no_changes"
  | "partial"
  | "incomplete_crawl"
  | "auth_failed"
  | "failed"
  | "running"
  | "unknown";

export type ReviewStatus = "new" | "accepted" | "rejected" | "done";

export type ArticlesSource = "pipeline" | "qdrant" | "none";

export interface SyncStatus {
  available: boolean;
  reason: string | null;
  result: RunResult;
  run_id: string | null;
  started_at: string | null;
  finished_at: string | null;
  age_hours: number | null;
  auth_ok: boolean | null;
  crawl_complete: boolean | null;
  articles_discovered: number | null;
  articles_changed: number | null;
  articles_unchanged: number | null;
  articles_deleted: number | null;
  docs_indexed: number | null;
  docs_skipped: number | null;
  docs_deleted: number | null;
  chunks_upserted: number | null;
  points_total: number | null;
  embed_model: string | null;
  embed_dim: number | null;
  collection: string | null;
  corpus_commit: string | null;
  duration_s: number | null;
  error_count: number;
  errors: Array<Record<string, unknown>>;
}

export interface IndexHealth {
  collection: string;
  status: string;
  points: number;
  articles: number;
  chunks: number;
  internal_articles: number;
  vector_size: number | null;
  distance: string | null;
  sparse: boolean;
  indexed_fields: string[];
  embed_model: string;
  embed_model_source: "env" | "pipeline" | "default";
}

export interface FacetRow {
  value: string;
  count: number;
}

export interface OverviewResponse {
  health: IndexHealth;
  sync: SyncStatus;
  categories: FacetRow[];
  article_statuses: FacetRow[];
  articles_source: ArticlesSource;
  newest_crawl: string | null;
  oldest_crawl: string | null;
}

export interface ArticleRow {
  document_id: string;
  title: string | null;
  url: string | null;
  description: string | null;
  category: string | null;
  category_path: string[];
  is_internal: boolean | null;
  access_rule: string | null;
  /** title_only / category_only when the two internal markers disagree. */
  internal_marker_conflict: string | null;
  hubspot_updated_at: string | null;
  crawl_date: string | null;
  word_count: number | null;
  chunk_count: number | null;
  indexed_at: string | null;
  status: string | null;
}

export interface ArticlesResponse {
  source: ArticlesSource;
  pipeline_available: boolean;
  reason: string | null;
  generated_at: string | null;
  count: number;
  articles: ArticleRow[];
}

export interface ArticleDetail extends ArticleRow {
  content_md: string | null;
  content_sha256: string | null;
}

export interface RunRow {
  run_id: string | null;
  result: RunResult;
  started_at: string | null;
  finished_at: string | null;
  auth_ok: boolean | null;
  crawl_complete: boolean | null;
  articles_discovered: number | null;
  articles_changed: number | null;
  articles_deleted: number | null;
  docs_indexed: number | null;
  docs_skipped: number | null;
  chunks_upserted: number | null;
  points_total: number | null;
  embed_model: string | null;
  duration_s: number | null;
  error_count: number | null;
}

export interface RunsResponse {
  available: boolean;
  reason: string | null;
  runs: RunRow[];
}

export interface SearchHit {
  score: number;
  document_id: string;
  title: string | null;
  url: string | null;
  category: string | null;
  category_path: string[];
  heading_path: string[];
  is_internal: boolean | null;
  hubspot_updated_at: string | null;
  crawl_date: string | null;
  chunk_index: number | null;
  chunk_count: number | null;
  text: string | null;
}

export interface SearchResponse {
  hits: SearchHit[];
  took_ms: number;
  embed_model: string;
}

export interface SearchBody {
  query: string;
  category?: string | null;
  include_internal?: boolean;
  limit?: number;
}

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

export interface ChatResponse {
  answer: string;
  model: string;
  citations: SearchHit[];
}

export interface AiModel {
  id: string;
  provider: string;
  name: string;
  is_default?: boolean;
}

// ── review desk ──────────────────────────────────────────────────────────────

export interface ReviewStateFields {
  status: ReviewStatus;
  note: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
}

export interface ReviewEvidence {
  kind: "code" | "tool" | "article" | "other";
  repo: string | null;
  file_path: string | null;
  line_start: number | null;
  line_end: number | null;
  name: string | null;
  url: string | null;
  note: string | null;
}

export type FindingKind =
  | "wrong"
  | "outdated"
  | "missing_detail"
  | "colour"
  | "fine";

export interface ReviewFinding {
  id: string;
  kind: FindingKind;
  passage: string | null;
  issue: string | null;
  suggested_rewrite: string | null;
  evidence: ReviewEvidence[];
}

export interface ArticleReview extends ReviewStateFields {
  review_id: string;
  document_id: string | null;
  title: string | null;
  url: string | null;
  category: string | null;
  hubspot_updated_at: string | null;
  verdict: "fine" | "wrong" | "outdated" | "missing_detail";
  summary: string | null;
  findings: ReviewFinding[];
  judge_model: string | null;
  cost_usd: number | null;
}

export interface ReviewDay {
  date: string;
  run_id: string | null;
  started_at: string | null;
  finished_at: string | null;
  judge_model: string | null;
  cost_usd: number | null;
  articles: ArticleReview[];
}

export interface ReviewQueue {
  new: number;
  accepted: number;
  rejected: number;
  done: number;
}

export interface ReviewsResponse {
  available: boolean;
  reason: string | null;
  generated_at: string | null;
  queue: ReviewQueue;
  days: ReviewDay[];
}

// ── release impact ───────────────────────────────────────────────────────────

export interface ScanRepoChange {
  repo: string;
  commits: number | null;
  files_changed: number | null;
}

export interface ScanCodeChange {
  repo: string | null;
  file_path: string | null;
  commit: string | null;
  author: string | null;
  date: string | null;
  summary: string | null;
}

export interface ScanImpact extends ReviewStateFields {
  id: string;
  document_id: string | null;
  title: string | null;
  url: string | null;
  category: string | null;
  confidence: "high" | "medium" | "low";
  why: string | null;
  changes: ScanCodeChange[];
  suggested_action: string | null;
}

export interface ScanNearArticle {
  document_id: string | null;
  title: string | null;
  url: string | null;
  score: number | null;
}

export interface ScanGap extends ReviewStateFields {
  id: string;
  capability: string;
  source_kind: "mcp_tool" | "route" | "other";
  source_name: string | null;
  coverage: "none" | "weak";
  nearest_articles: ScanNearArticle[];
  outline: string | null;
}

export interface ScanRun {
  scan_id: string;
  scanned_at: string | null;
  window_since: string | null;
  window_until: string | null;
  repos: ScanRepoChange[];
  commits_total: number | null;
  chunks_changed: number | null;
  triage_model: string | null;
  judge_model: string | null;
  cost_usd: number | null;
  impacts: ScanImpact[];
  gaps: ScanGap[];
}

export interface ScansResponse {
  available: boolean;
  reason: string | null;
  generated_at: string | null;
  scans: ScanRun[];
}

// ── hooks ────────────────────────────────────────────────────────────────────

export function useKbOverview() {
  return useQuery({
    queryKey: ["kb_rag", "overview"],
    queryFn: () => apiFetch<OverviewResponse>("/kb-rag/overview"),
    // The index moves once a night; go stale within a session so a manual
    // re-run shows up without a hard reload.
    staleTime: 5 * 60_000,
  });
}

export function useKbArticles(params: {
  category?: string;
  status?: string;
  q?: string;
}) {
  const search = new URLSearchParams();
  if (params.category) search.set("category", params.category);
  if (params.status) search.set("status", params.status);
  if (params.q) search.set("q", params.q);
  const qs = search.toString();
  return useQuery({
    queryKey: [
      "kb_rag",
      "articles",
      params.category ?? "",
      params.status ?? "",
      params.q ?? "",
    ],
    queryFn: () =>
      apiFetch<ArticlesResponse>(`/kb-rag/articles${qs ? `?${qs}` : ""}`),
    staleTime: 5 * 60_000,
  });
}

export function useKbArticle(documentId: string | undefined) {
  return useQuery({
    queryKey: ["kb_rag", "article", documentId ?? ""],
    queryFn: () => apiFetch<ArticleDetail>(`/kb-rag/articles/${documentId}`),
    enabled: Boolean(documentId),
    staleTime: 30 * 60_000,
  });
}

export function useKbRuns(limit = 60) {
  return useQuery({
    queryKey: ["kb_rag", "runs", limit],
    queryFn: () => apiFetch<RunsResponse>(`/kb-rag/runs?limit=${limit}`),
    staleTime: 5 * 60_000,
  });
}

/** Search is a QUERY keyed on the URL state — see code_rag/api.ts for why. */
export function useKbSearch(params: SearchBody) {
  const query = params.query.trim();
  return useQuery({
    queryKey: [
      "kb_rag",
      "search",
      query,
      params.category ?? "",
      params.limit ?? 10,
    ],
    queryFn: () =>
      apiFetch<SearchResponse>("/kb-rag/search", {
        method: "POST",
        body: { ...params, query },
      }),
    enabled: query.length > 0,
    staleTime: 5 * 60_000,
  });
}

export function useKbAsk() {
  return useMutation({
    mutationFn: (body: {
      messages: ChatMessage[];
      category?: string | null;
      model_id?: string | null;
      context_chunks?: number;
    }) => apiFetch<ChatResponse>("/kb-rag/ask", { method: "POST", body }),
  });
}

export function useKbModels() {
  return useQuery({
    queryKey: ["kb_rag", "models"],
    queryFn: () =>
      apiFetch<{ models: AiModel[]; default_id: string | null }>(
        "/kb-rag/models",
      ),
    staleTime: Infinity,
  });
}

export function useKbReviews() {
  return useQuery({
    queryKey: ["kb_rag", "reviews"],
    queryFn: () => apiFetch<ReviewsResponse>("/kb-rag/reviews?days=60"),
    // The desk is worked interactively; a fresh read on focus is right here.
    staleTime: 60_000,
  });
}

export function useSetReviewState() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      reviewId: string;
      status: ReviewStatus;
      note: string;
    }) =>
      apiFetch<ReviewStateFields>(
        `/kb-rag/reviews/${encodeURIComponent(vars.reviewId)}`,
        {
          method: "PATCH",
          body: { status: vars.status, note: vars.note },
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["kb_rag", "reviews"] }),
  });
}

export function useKbScans() {
  return useQuery({
    queryKey: ["kb_rag", "scans"],
    queryFn: () => apiFetch<ScansResponse>("/kb-rag/scans?limit=12"),
    staleTime: 5 * 60_000,
  });
}

export function useSetScanItemState() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      kind: "scan_impact" | "scan_gap";
      itemId: string;
      status: ReviewStatus;
      note: string;
    }) =>
      apiFetch<ReviewStateFields>(
        `/kb-rag/scans/items/${vars.kind}/${encodeURIComponent(vars.itemId)}`,
        { method: "PATCH", body: { status: vars.status, note: vars.note } },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["kb_rag", "scans"] }),
  });
}
